import { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Spin } from "antd";
import { ApiClientContext } from "../../../App";
import { SummaryReportDTO, SummaryReportPayload } from "../types";
import {
  getColor,
  getResolution,
  getBucketKey,
  formatBucketLabel,
  toApiStartDate,
  toApiEndDate,
} from "../utils";
import StatsBar, { GroupBy } from "./StatsBar";
import ActivityChart from "./ActivityChart";
import BreakdownTable from "./BreakdownTable";
import DonutChart from "./DonutChart";

const Summary = () => {
  const location = useLocation();
  const { apiClient } = useContext(ApiClientContext);
  const [data, setData] = useState<SummaryReportDTO | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [groupBy, setGroupBy] = useState<GroupBy>("project");

  const params = new URLSearchParams(location.search);
  const startDate = params.get("startDate") ?? "";
  const endDate = params.get("endDate") ?? "";
  const projectIds = params.get("projectIds") ?? "";
  const employeeIds = params.get("employeeIds") ?? "";

  useEffect(() => {
    if (!startDate || !endDate) return;

    const payload: SummaryReportPayload = {
      startDate: toApiStartDate(startDate),
      endDate: toApiEndDate(endDate),
      projectIds: projectIds ? projectIds.split(",") : [],
      employeeIds: employeeIds ? employeeIds.split(",") : [],
    };

    const fetchReport = async () => {
      try {
        setIsLoading(true);
        const { data: responseData } = await apiClient.post(
          "/api/time-tracking/reports/summary",
          payload,
        );
        setData(responseData);
      } catch (err) {
        console.warn(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchReport();
  }, [startDate, endDate, projectIds, employeeIds]);

  const breakdown =
    groupBy === "project"
      ? data?.projectBreakdown ?? []
      : data?.userBreakdown ?? [];

  const breakdownKeys = breakdown.map((item, i) => ({
    key:
      groupBy === "project"
        ? (item as any).projectName
        : (item as any).fullName,
    color: getColor((item as any).color, i),
    minutes: item.durationMinutes,
    totalEntries: (item as any).totalEntries ?? 0,
  }));

  const resolution = getResolution(startDate, endDate);

  const barData = (() => {
    const buckets: Record<string, Record<string, number>> = {};
    if (groupBy === "project") {
      for (const day of data?.days ?? []) {
        const key = getBucketKey(day.date, resolution);
        if (!buckets[key]) buckets[key] = {};
        day.projects.forEach((p) => {
          buckets[key][p.projectName] =
            (buckets[key][p.projectName] ?? 0) + p.durationMinutes;
        });
      }
    } else {
      for (const day of data?.days ?? []) {
        const key = getBucketKey(day.date, resolution);
        if (!buckets[key]) buckets[key] = {};
      }
      for (const user of data?.userBreakdown ?? []) {
        for (const d of user?.days) {
          const key = getBucketKey(d.date, resolution);
          if (!buckets[key]) buckets[key] = {};
          buckets[key][user?.fullName] =
            (buckets[key][user?.fullName] ?? 0) + d.durationMinutes;
        }
      }
    }
    return Object.entries(buckets)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, values]) => ({
        date: formatBucketLabel(key, resolution),
        ...values,
      }));
  })();

  const donutData = breakdownKeys.map((item) => ({
    name: item.key,
    value: item.minutes,
    color: item.color,
  }));

  return (
    <Spin spinning={isLoading}>
      <StatsBar data={data} groupBy={groupBy} onGroupByChange={setGroupBy} />
      <ActivityChart barData={barData} breakdownKeys={breakdownKeys} />
      <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
        <BreakdownTable items={breakdownKeys} />
        <DonutChart
          data={donutData}
          totalDurationMinutes={data?.totalDurationMinutes ?? null}
        />
      </div>
    </Spin>
  );
};

export default Summary;
